import './AppHeader.css';

import { Avatar, Badge, Button, Divider, Dropdown, Empty, Flex, Input, List, Popover, Space, Tooltip, Typography, theme } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMyApprovalListApi } from '@services/SereviseConst';
import { getAdminInfo, numberProject } from '@utils/utils';
import { useAppBreakpoint } from '@/contexts/BreakpointContext';
import { useThemeMode } from '@/contexts/ThemeContext';

function PanelLeftCloseIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 3v18" />
            <path d="m16 15-3-3 3-3" />
        </svg>
    );
}

function PanelLeftOpenIcon() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 3v18" />
            <path d="m14 9 3 3-3 3" />
        </svg>
    );
}

function isMacPlatform() {
    if (typeof navigator === 'undefined') return false;
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform);
}

function getStatusColor(approvalStatus) {
    const map = {
        0: '#999999',
        1: '#FA8C16',
        2: '#52C41A',
        3: '#FF4D4F',
    };
    return map[approvalStatus] ?? '#FA8C16';
}

function formatCount(count) {
    return count > 99 ? '99+' : count;
}

export default function AppHeader({ siderCollapsed, onToggleSider, onOpenSearch }) {
    const { token } = theme.useToken();
    const { isMobile } = useAppBreakpoint();
    const { mode: themeMode, resolvedMode, setMode } = useThemeMode();
    const navigate = useNavigate();
    const location = useLocation();

    const { adminName, adminPhone } = useMemo(() => getAdminInfo(), []);
    const displayName = adminName || adminPhone || 'Admin';

    const [batchList, setBatchList] = useState([]);
    const [approvalCount, setApprovalCount] = useState(0);
    const pollingRef = useRef(null);
    const fetchingRef = useRef(false);
    const isMountedRef = useRef(false);

    /**
     * 待审批批次轮询
     *
     * 背景：标签页长时间在后台仍每 10s 请求 myApprovalList，切回前台时 interval、
     * visibility、路由变化可能叠加，休眠/断网恢复瞬间会并发多次失败（控制台 ERR_INTERNET_DISCONNECTED）。
     *
     * 策略：
     * - 页面 hidden 时停止轮询，visible / online 时拉一次并重启
     * - offline 或已有进行中的请求时跳过，避免重复打接口
     */
    const fetchPendingBatches = useCallback(async () => {
        // 浏览器判定离线时不请求
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return;
        }
        // 防止 interval、visibility、路由切换同时触发并发请求
        if (fetchingRef.current) {
            return;
        }
        fetchingRef.current = true;
        try {
            const res = await getMyApprovalListApi();
            if (res && res.status_code === 200 && res.data) {
                const dataList = res.data || [];
                setBatchList(dataList);
                setApprovalCount(Array.isArray(dataList) ? dataList.length : 0);
            }
        } catch {
            // silent fail
        } finally {
            fetchingRef.current = false;
        }
    }, []);

    const stopPolling = useCallback(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        // 后台标签页不启动 interval
        if (pollingRef.current || document.hidden) {
            return;
        }
        pollingRef.current = setInterval(fetchPendingBatches, 10000);
    }, [fetchPendingBatches]);

    useEffect(() => {
        const resumePolling = () => {
            fetchPendingBatches();
            startPolling();
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                stopPolling();
                return;
            }
            // 切回前台：立即刷新一次并恢复 10s 轮询
            resumePolling();
        };

        const handleOnline = () => {
            if (!document.hidden) {
                resumePolling();
            }
        };

        if (!document.hidden) {
            resumePolling();
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnline);

        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
        };
    }, [fetchPendingBatches, startPolling, stopPolling]);

    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            return;
        }
        fetchPendingBatches();
    }, [location.pathname, fetchPendingBatches]);

    const handleBatchClick = useCallback(
        batch => {
            if (!batch) return;
            const timestamp = Date.now();
            const fromPageQuery = `&fromPage=${encodeURIComponent(location.pathname)}`;
            if (batch.nodeId === 5 || batch.nodeId === 6) {
                navigate(`/finance/laborfee/projects?openApprovalBatch=true&projectId=${batch.conferenceId}&batchNo=${batch.batchNo}${fromPageQuery}&t=${timestamp}`);
            } else if (batch.nodeId === 10) {
                navigate(`/finance/laborfee/projects?openPaymentBatch=true&projectId=${batch.conferenceId}&batchNo=${batch.batchNo}${fromPageQuery}&t=${timestamp}`);
            } else {
                navigate(`/conf/conferences/${batch.conferenceId}/laborfee?openPaymentBatch=true&batchNo=${batch.batchNo}${fromPageQuery}&t=${timestamp}`);
            }
        },
        [location.pathname, navigate],
    );

    const handleLogout = useCallback(() => {
        localStorage.removeItem('infos');
        localStorage.removeItem('token');
        localStorage.removeItem('permissionsList');
        const returnUrl = location.pathname + location.search;
        const url = returnUrl !== '/' ? `/login?returnurl=${returnUrl}` : '/login';
        navigate(url);
    }, [location, navigate]);

    const shortcutKeys = isMacPlatform() ? ['⌘', 'K'] : ['Ctrl', 'K'];
    const headerIconTokenStyle = {
        '--app-header-icon-btn-hover-bg': token.colorFillSecondary,
        '--app-header-icon-btn-active-bg': token.colorFillTertiary,
    };

    const wrapHeaderTooltip = useCallback(
        (title, node) => {
            if (isMobile) {
                return node;
            }
            return (
                <Tooltip title={title} mouseEnterDelay={0.3} placement="bottom">
                    {node}
                </Tooltip>
            );
        },
        [isMobile],
    );

    const themeOptions = useMemo(
        () => [
            { key: 'light', title: '浅色模式', description: '始终使用浅色主题', icon: 'bi-sun' },
            { key: 'dark', title: '深色模式', description: '始终使用深色主题', icon: 'bi-moon' },
            { key: 'system', title: '自动模式', description: '跟随系统主题设置', icon: 'bi-display' },
        ],
        [],
    );

    const themeMenuItems = useMemo(
        () => [
            ...themeOptions.map(option => ({
                key: option.key,
                icon: <span className={`bi ${option.icon}`} aria-hidden />,
                label: (
                    <Flex vertical gap={0}>
                        <Typography.Text strong>{option.title}</Typography.Text>
                        <Typography.Text type="secondary">{option.description}</Typography.Text>
                    </Flex>
                ),
            })),
            { type: 'divider' },
            {
                key: 'theme-system-status',
                disabled: true,
                label: <Typography.Text type="secondary">当前跟随系统：{resolvedMode === 'dark' ? '深色' : '浅色'}</Typography.Text>,
            },
        ],
        [themeOptions, resolvedMode],
    );

    const handleThemeMenuClick = useCallback(
        ({ key }) => {
            if (key === 'light' || key === 'dark' || key === 'system') {
                setMode(key);
            }
        },
        [setMode],
    );

    const themeIconClassName = themeMode === 'system' ? 'bi-display' : themeMode === 'dark' ? 'bi-moon-stars' : 'bi-sun';

    const themeDropdown = (
        <Dropdown trigger={isMobile ? ['click'] : ['hover']} placement="bottomRight" menu={{ items: themeMenuItems, selectedKeys: [themeMode], onClick: handleThemeMenuClick }} mouseEnterDelay={0.1} mouseLeaveDelay={0.15}>
            <Button type="text" className="app-header-icon-action" aria-label="切换主题">
                <span className={`bi ${themeIconClassName}`} style={{ fontSize: 16 }} aria-hidden />
            </Button>
        </Dropdown>
    );

    const userMenuItems = useMemo(
        () => [
            {
                key: 'user-overview',
                disabled: true,
                style: {
                    cursor: 'default',
                    height: 'auto',
                    opacity: 1,
                    pointerEvents: 'none',
                    paddingInline: `${token.padding}px`,
                    paddingBlock: token.paddingSM,
                    backgroundColor: token.colorBgElevated,
                    borderBottom: `1px solid ${token.colorSplit}`,
                },
                label: (
                    <Flex align="center" gap={8}>
                        <Typography.Text strong style={{ color: token.colorText }}>
                            {displayName}
                        </Typography.Text>
                    </Flex>
                ),
            },
            { key: 'profile', label: '个人信息' },
            { key: 'changepassword', label: '修改密码' },
            { type: 'divider' },
            { key: 'logout', label: '退出登录', danger: true },
        ],
        [token, displayName],
    );

    const handleUserMenuClick = useCallback(
        ({ key }) => {
            if (key === 'profile') navigate('/my/messages');
            if (key === 'changepassword') navigate('/my/changepassword');
            if (key === 'logout') handleLogout();
        },
        [navigate, handleLogout],
    );

    const toggleSiderTitle = isMobile ? '打开主菜单' : siderCollapsed ? '展开侧边栏' : '收起侧边栏';

    const toggleSiderButton = wrapHeaderTooltip(
        toggleSiderTitle,
        <Button
            id="app-header-toggle-sider"
            className="app-header-icon-action"
            type="text"
            icon={<span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, lineHeight: 0, color: 'inherit' }}>{isMobile ? <span className="bi bi-list" aria-hidden /> : siderCollapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}</span>}
            onClick={onToggleSider}
            style={{ flexShrink: 0 }}
            aria-label={toggleSiderTitle}
        />,
    );

    const userDropdown = (
        <Dropdown trigger={[isMobile ? 'click' : 'hover']} placement="bottomRight" menu={{ items: userMenuItems, onClick: handleUserMenuClick }} mouseEnterDelay={0.1} mouseLeaveDelay={0.15} styles={{ root: { minWidth: 150 } }}>
            <Button type="text" className="app-header-icon-action" aria-label="打开用户菜单">
                <Avatar size="small" style={{ background: token.colorPrimary }}>
                    {displayName ? displayName.charAt(0).toUpperCase() : 'A'}
                </Avatar>
            </Button>
        </Dropdown>
    );

    if (isMobile) {
        return (
            <Flex
                id="app-header"
                align="center"
                style={{
                    height: 56,
                    padding: `0 ${token.padding}px`,
                    flexShrink: 0,
                    background: token.colorBgContainer,
                    borderBottom: `1px solid ${token.colorSplit}`,
                    ...headerIconTokenStyle,
                }}
            >
                <Flex flex="1 1 0" justify="flex-start" align="center">
                    {toggleSiderButton}
                </Flex>
                <Flex flex="0 0 auto" align="center" gap={8} justify="center">
                    <img src="/logo.svg" alt="logo" style={{ width: 24, height: 24 }} />
                    <Typography.Text strong style={{ fontSize: 16, color: token.colorTextHeading }}>
                        华媒康讯
                    </Typography.Text>
                </Flex>
                <Flex flex="1 1 0" justify="flex-end" align="center" gap={4}>
                    {themeDropdown}
                    {userDropdown}
                </Flex>
            </Flex>
        );
    }

    const notificationContent = (
        <div style={{ width: 320 }}>
            <Flex justify="space-between" align="center">
                <Typography.Text strong>待处理</Typography.Text>
                <Typography.Text type="secondary">{formatCount(approvalCount)} 条待处理</Typography.Text>
            </Flex>
            {batchList.length > 0 ? (
                <List
                    style={{ marginTop: 8, maxHeight: 360, overflowY: 'auto' }}
                    size="small"
                    split
                    dataSource={batchList}
                    renderItem={batch => {
                        const color = getStatusColor(batch.approvalStatus);
                        return (
                            <List.Item style={{ cursor: 'pointer', paddingInline: 0 }} onClick={() => handleBatchClick(batch)}>
                                <Flex vertical gap={2} style={{ width: '100%' }}>
                                    <Typography.Text>付款批次：{batch.batchCreatedAt || '—'}</Typography.Text>
                                    <Typography.Text strong>{batch.conferenceName}</Typography.Text>
                                    <Typography.Text type="secondary">会议项目编号：{numberProject('YHY', batch.conferenceId)}</Typography.Text>
                                    {batch.nodeId === 10 && (
                                        <Typography.Text type="secondary">
                                            付款明细打印：
                                            {batch.printedAt ? <Typography.Text type="success">已打印</Typography.Text> : <Typography.Text type="warning">未打印</Typography.Text>}
                                        </Typography.Text>
                                    )}
                                    <Flex gap={8}>
                                        <Typography.Text style={{ color }}>{batch.nodeName}</Typography.Text>
                                        <Typography.Text style={{ color }}>{batch.approvalStatusName}</Typography.Text>
                                    </Flex>
                                </Flex>
                            </List.Item>
                        );
                    }}
                />
            ) : (
                <Empty description="暂无待处理任务" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
            )}
        </div>
    );

    return (
        <Flex
            id="app-header"
            align="center"
            gap={12}
            style={{
                height: 56,
                padding: `0 ${token.padding}px`,
                flexShrink: 0,
                background: token.colorBgContainer,
                borderBottom: `1px solid ${token.colorSplit}`,
                ...headerIconTokenStyle,
            }}
        >
            {toggleSiderButton}

            <span aria-hidden style={{ width: 1, height: 20, margin: 0, flexShrink: 0, background: token.colorSplit }} />

            <Input
                id="app-header-search"
                readOnly
                aria-label="打开搜索面板"
                placeholder="搜索后台功能..."
                prefix={<span className="bi bi-search" aria-hidden />}
                suffix={
                    <Space size={0}>
                        {shortcutKeys.map(key => (
                            <Typography.Text key={key} keyboard>
                                {key}
                            </Typography.Text>
                        ))}
                    </Space>
                }
                onClick={onOpenSearch}
                style={{ flex: '1 1 200px', minWidth: 160, maxWidth: 300, cursor: 'pointer' }}
            />

            <Flex id="app-header-actions" align="center" gap={8} style={{ marginLeft: 'auto', flexShrink: 0 }}>
                <Popover trigger="click" placement="bottomRight" content={notificationContent} styles={{ container: { padding: 12 } }}>
                    {wrapHeaderTooltip(
                        '待处理通知',
                        <Button type="text" className="app-header-icon-action" aria-label="查看待处理通知">
                            <Badge count={formatCount(approvalCount)} overflowCount={99}>
                                <span className="bi bi-bell" style={{ fontSize: 16 }} />
                            </Badge>
                        </Button>,
                    )}
                </Popover>

                {themeDropdown}
                {userDropdown}
            </Flex>
        </Flex>
    );
}
